import { type OperationVariables } from '@apollo/react-common';
import { type GraphQLTaggedNode } from 'react-relay';

import {
	createCacheSnapshotRestore,
	recordOptimisticInteropWrite,
} from './optimisticInteropWriteRollback.ts';

const fragmentObjectIds = new WeakMap<object, number>();
let nextFragmentObjectId = 0;

// Builds the fragment-specific restore point used by writeFragmentInterop.
// Fragment optimistic updates are entity-scoped, so the key includes the
// fragment artifact, record id, variables, and caller package.
export function recordOptimisticFragmentWriteRollback<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
>({
	fragment,
	fragmentId,
	variables,
	callerPackage,
	readPreviousData,
	writePreviousData,
	writeData,
}: {
	fragment: GraphQLTaggedNode;
	fragmentId: string;
	variables?: TVariables;
	callerPackage?: string;
	readPreviousData: () => TData | null;
	writePreviousData: (data: TData) => void;
	writeData: () => void;
}): void {
	recordOptimisticInteropWrite({
		getKey: () =>
			getOptimisticFragmentWriteKey({
				fragment,
				fragmentId,
				variables,
				callerPackage,
			}),
		createRestore: () => {
			let previousData: TData | null;
			try {
				// Prefer restoring the exact fragment record when it is readable.
				// Fragment writes are entity-scoped, so this is usually the smallest
				// rollback that can undo the optimistic update.
				previousData = readPreviousData();
			} catch {
				// Fragment reads can throw when the record or selected fields are not
				// present. Treat that like an unreadable previous state and fall back
				// to the broader snapshot path so the optimistic write is still
				// covered by rollback.
				return createCacheSnapshotRestore();
			}

			if (previousData == null) {
				// No previous fragment result means the optimistic write may be
				// creating new entity/cache shape. Snapshot rollback is safer than
				// trying to synthesize a "delete" through fragment APIs.
				return createCacheSnapshotRestore();
			}

			return () => {
				// Restore through writeFragmentInterop so rollback follows the same
				// route and typename handling as a normal fragment interop write.
				writePreviousData(previousData);
			};
		},
		applyWrite: writeData,
	});
}

function getOptimisticFragmentWriteKey<
	TVariables extends Record<string, unknown> = OperationVariables,
>({
	fragment,
	fragmentId,
	variables,
	callerPackage,
}: {
	fragment: GraphQLTaggedNode;
	fragmentId: string;
	variables?: TVariables;
	callerPackage?: string;
}) {
	const fragmentObject = fragment as object;
	if (!fragmentObjectIds.has(fragmentObject)) {
		// Relay fragment artifacts are runtime objects. WeakMap IDs let us build a
		// dedupe key without depending on private artifact fields or retaining the
		// artifact after it is otherwise collectible.
		fragmentObjectIds.set(fragmentObject, nextFragmentObjectId++);
	}

	return JSON.stringify({
		fragment: fragmentObjectIds.get(fragmentObject),
		fragmentId,
		variables: variables ?? {},
		callerPackage: callerPackage ?? null,
	});
}
